import { Fragment } from 'react';
import './postseason-race.styles.scss';
import { Link } from 'react-router-dom';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import TeamName from '@/components/SHARED/team-name/team-name.component';
import { gamesAhead, postseasonRace } from '@shared/postseasonRace.js';

// How many teams past the line each table shows.
const NEXT_OUT = 4;

// The postseason race during the regular season: each conference's teams
// in seeding order down to the cutoff line (college: the conference
// tournament; pro: the playoffs), then the next four out. Uses the same
// order startPostseason seeds from. teams: summary/teams `teams` map.
const PostseasonRace = ({ league, teams, teamLink, highlightId }) => {

    if (!league || !teams) return null;
    const mine = (g) => (g.teamIds.includes(highlightId) ? 0 : 1);
    const groups = postseasonRace(league, teams)
        .sort((a, b) => mine(a) - mine(b) || a.name.localeCompare(b.name));

    return (
        <div className='postseason-race'>
            {groups.map(g => <RaceGroup key={g.id} group={g} teams={teams} teamLink={teamLink} highlightId={highlightId} />)}
        </div>
    )
}

const RaceGroup = ({ group, teams, teamLink, highlightId }) => {
    const { teamIds, cutoff, byes, kind } = group;
    const college = kind !== 'playoffs';
    const everyone = cutoff >= teamIds.length;
    const lastIn = teams[teamIds[cutoff - 1]];
    const firstOut = teams[teamIds[cutoff]];
    const myIndex = teamIds.indexOf(highlightId);
    const shown = teamIds.slice(0, cutoff + NEXT_OUT);
    // The manager's team further down still gets a row, after a gap.
    const extra = myIndex >= shown.length ? highlightId : null;
    const cols = college ? 5 : 4;

    const title = group.id === 'league' ? 'Playoff race' : group.name;
    const prize = kind === 'auto-bid' ? 'the automatic bid' :
        kind === 'playoffs' ? 'the playoffs' : `the ${group.name} Tournament`;
    const lineLabel = kind === 'auto-bid' ? 'Automatic bid' :
        kind === 'playoffs' ? 'Playoff line' : 'Tournament line';
    let note;
    if (kind === 'auto-bid') {
        note = 'The regular-season champion gets the automatic bid to the national tournament.';
    } else if (everyone) {
        note = `All ${teamIds.length} teams make ${prize}.`;
    } else {
        const where = kind === 'playoffs' && group.id !== 'league' ? ' in the conference' : '';
        note = `The top ${cutoff}${where} make ${prize}`;
        if (byes) note += `; the top ${byes} get a first-round bye`;
        note += '.';
    }
    note += college ? ' Seeded by conference record.' : ' Seeded by record.';

    // Half games: positive is ahead of the first team out, negative behind
    // the last team in.
    const margin = (i) => {
        const r = teams[teamIds[i]].record;
        const games = i < cutoff ?
            (firstOut ? gamesAhead(r, firstOut.record, college) : null) :
            gamesAhead(r, lastIn.record, college);
        if (games === null) return '';
        return games > 0 ? `+${games}` : `${games}`;
    };

    const row = (id) => {
        const i = teamIds.indexOf(id);
        const t = teams[id];
        const r = t.record;
        return (
            <tr key={id} className={`${id === highlightId ? 'highlight' : ''} ${i >= cutoff ? 'out' : ''}`}>
                <td className='num'>{i + 1}</td>
                <td className='team-col'>
                    <Link to={teamLink(id)} className='team-link'>
                        <TeamBadge team={t} size='sm' />
                        <span><TeamName team={t} /></span>
                    </Link>
                    {i < byes && <span className='bye-tag'>bye</span>}
                </td>
                {college && <td className='num'>{r.confW}-{r.confL}</td>}
                <td className='num'>{r.w}-{r.l}</td>
                <td className='num'>{everyone ? '' : margin(i)}</td>
            </tr>
        );
    };

    return (
        <section className='g-card race-group'>
            <h3>{title}</h3>
            <p className='meta'>{note}</p>
            <div className='table-scroll'>
                <table className='g-table race-table'>
                    <thead>
                        <tr>
                            <th className='num'>#</th>
                            <th className='team-col'>Team</th>
                            {college && <th className='num'>Conf</th>}
                            <th className='num'>{college ? 'Overall' : 'W-L'}</th>
                            <th className='num' title='Games ahead of the first team out, or behind the last team in'>Line</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((id, i) => (
                            <Fragment key={id}>
                                {row(id)}
                                {i === cutoff - 1 && !everyone &&
                                    <tr className='cutoff-row'><td colSpan={cols}><span>{lineLabel}</span></td></tr>
                                }
                            </Fragment>
                        ))}
                        {extra &&
                            <>
                                <tr className='gap-row'><td colSpan={cols}>…</td></tr>
                                {row(extra)}
                            </>
                        }
                    </tbody>
                </table>
            </div>
        </section>
    )
}

export default PostseasonRace
